import React, { useState, useMemo, useEffect } from 'react';
import { Button, Collapse, Badge, Form } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import AddMemberModal from './AddMemberModal';
import { toast, ToastContainer } from 'react-toastify';
import { recalcularBonus } from '../utils/api';
import { DICT } from '../utils/dictionary'; // Importação do dicionário
import 'react-toastify/dist/ReactToastify.css';

const SORT_ICONS = (dir) => dir === 'asc' ? <i className="bi bi-caret-up-fill"></i> : <i className="bi bi-caret-down-fill"></i>;

// White Label: Prefixo de storage dinâmico
const STORAGE_PREFIX = process.env.REACT_APP_STORAGE_PREFIX || 'app';
const LS_KEY_GLOBAL = `${STORAGE_PREFIX}_videos_pageSize`;
const LS_KEY_USER = (user) => `${STORAGE_PREFIX}_videos_pageSize_${(user?.nome || 'anon').toUpperCase()}`;

const validatePageSize = (v) => [10, 25, 50, 100].includes(v) ? v : 25;

const VideoTable = ({ videos, setVideos = () => {}, isDemo }) => {
  const { user, token } = useAuth();
  const isAdmin = user?.acesso === 'admin';

  const [limite, setLimite] = useState(() => {
    const saved = Number(localStorage.getItem(LS_KEY_USER(user)) || localStorage.getItem(LS_KEY_GLOBAL));
    return validatePageSize(saved);
  });

  const [expanded, setExpanded] = useState({});
  const [filtroStatus, setFiltroStatus] = useState('');
  const [pagina, setPagina] = useState(1);
  const [modalAberto, setModalAberto] = useState(false);
  const [videoSelecionado, setVideoSelecionado] = useState(null);

  const [sortKey, setSortKey] = useState('date');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    const saved = Number(localStorage.getItem(LS_KEY_USER(user)));
    if (saved) setLimite(validatePageSize(saved));
  }, [user]);

  useEffect(() => {
    localStorage.setItem(LS_KEY_GLOBAL, String(limite));
    localStorage.setItem(LS_KEY_USER(user), String(limite));
  }, [limite, user]);

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  const abrirModal = (videoId) => { setVideoSelecionado(videoId); setModalAberto(true); };

  // Função Robusta para Formatar Data
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr.includes('T') ? dateStr : `${dateStr}T00:00:00-03:00`);
      return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('pt-BR');
    } catch { return '-'; }
  };

  const adicionarMembro = async ({ nome, cargo }) => {
    const video = videos.find(v => v.id === videoSelecionado);
    if (video?.equipe?.some(m => m.nome === nome)) {
      toast.error(`Este membro já está atribuído a este ${DICT.CONTENT_SING.toLowerCase()}.`);
      return;
    }

    if (isDemo) {
      setVideos(prev => prev.map(v => v.id === videoSelecionado ?
        { ...v, equipe: [...(v.equipe || []), { id: Date.now(), nome, cargo, bonus: 0, status: 'pendente' }] } : v
      ));
      setExpanded(prev => ({ ...prev, [videoSelecionado]: true }));
      toast.info('Simulação: Membro adicionado localmente.');
      return;
    }

    const response = await fetch(`/api/videos/${videoSelecionado}/adicionar_membro`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ nome, cargo })
    });

    if (response.ok) {
      const atualizado = await response.json();
      setVideos(prev => prev.map(v => v.id === videoSelecionado ? { ...v, equipe: atualizado.equipe } : v));
      setExpanded(prev => ({ ...prev, [videoSelecionado]: true }));
      toast.success('Membro adicionado com sucesso!');
    }
  };

  const removerMembro = async (videoId, usuarioId) => {
    if (isDemo) {
      setVideos(prev => prev.map(v => v.id === videoId ?
        { ...v, equipe: v.equipe.filter(m => m.id !== usuarioId) } : v
      ));
      toast.info('Simulação: Membro removido localmente.');
      return;
    }

    const response = await fetch(`/api/videos/${videoId}/remover_membro`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ usuario_id: usuarioId })
    });

    if (response.ok) {
      const atualizado = await response.json();
      setVideos(prev => prev.map(v => v.id === videoId ? { ...v, equipe: atualizado.equipe } : v));
      toast.success('Membro removido!');
    }
  };

  const getTotalBonus = (v) => (v.equipe?.reduce((acc, c) => acc + (Number(c.bonus) || 0), 0) || 0);

  // Filtros e Paginação
  const filtrados = useMemo(() => {
    return videos.filter(v => !filtroStatus || v.status === filtroStatus);
  }, [videos, filtroStatus]);

  const ordenados = useMemo(() => {
    const sorted = [...filtrados];
    sorted.sort((a, b) => {
      if (sortKey === 'date') {
        const da = new Date(a.data_publicacao || a.date || 0);
        const db = new Date(b.data_publicacao || b.date || 0);
        return sortDir === 'asc' ? da - db : db - da;
      }
      if (sortKey === 'views') {
        return sortDir === 'asc' ? (a.views || 0) - (b.views || 0) : (b.views || 0) - (a.views || 0);
      }
      if (sortKey === 'title') {
        const ta = (a.titulo || a.title || '').toLowerCase();
        const tb = (b.titulo || b.title || '').toLowerCase();
        return sortDir === 'asc' ? ta.localeCompare(tb) : tb.localeCompare(ta);
      }
      if (sortKey === 'status') {
        const sa = (a.status || '').toLowerCase();
        const sb = (b.status || '').toLowerCase();
        return sortDir === 'asc' ? sa.localeCompare(sb) : sb.localeCompare(sa);
      }
      return 0;
    });
    return sorted;
  }, [filtrados, sortKey, sortDir]);

  const inicio = (pagina - 1) * limite;
  const paginados = ordenados.slice(inicio, inicio + limite);
  const totalPaginas = Math.max(1, Math.ceil(ordenados.length / limite));

  const onChangeSort = (key) => {
    setSortKey(key);
    setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    setPagina(1);
  };

  return (
    <div className="border-0">
      <div className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
          <h5 className="mb-0 fw-bold" style={{ color: 'var(--brand-secondary)' }}>{DICT.CONTENT_PLUR} e Produção</h5>
          <Form.Select
            size="sm"
            style={{ width: '180px' }}
            value={filtroStatus}
            onChange={(e) => { setFiltroStatus(e.target.value); setPagina(1); }}
          >
            <option value="">Todos os status</option>
            <option value="pendente">Pendente</option>
            <option value="pago">Pago</option>
          </Form.Select>
        </div>

        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead className="bg-light text-secondary small text-uppercase">
              <tr>
                <th style={{ width: 100 }}>Equipe</th>
                <th role="button" onClick={() => onChangeSort('date')}>Data {sortKey === 'date' && SORT_ICONS(sortDir)}</th>
                <th role="button" onClick={() => onChangeSort('title')}>Título {sortKey === 'title' && SORT_ICONS(sortDir)}</th>
                <th>Tipo</th>
                <th role="button" onClick={() => onChangeSort('views')}>{DICT.METRIC_PLUR} {sortKey === 'views' && SORT_ICONS(sortDir)}</th>
                <th>{DICT.REWARD} Total</th>
                <th role="button" onClick={() => onChangeSort('status')}>Status {sortKey === 'status' && SORT_ICONS(sortDir)}</th>
                {isAdmin && <th>Ações</th>}
              </tr>
            </thead>
            <tbody>
              {paginados.map(video => {
                const totalBonus = getTotalBonus(video);
                const badgeColor = video.status === 'pago' ? 'bg-success' : 'bg-warning text-dark';

                // Resolve as chaves dinâmicas do Mock vs Real
                const videoTitle = video.titulo || video.title || 'Sem título';
                const videoDate = video.data_publicacao || video.date;
                const videoViews = video.views || 0;

                return (
                  <React.Fragment key={video.id}>
                    <tr className="border-bottom">
                      <td>
                        <Button
                          variant="light"
                          size="sm"
                          onClick={() => toggleExpand(video.id)}
                          className="border shadow-sm"
                        >
                          <i className="bi bi-people-fill" style={{ color: 'var(--brand-primary)' }}></i>
                          <Badge bg="secondary" className="ms-1">{video.equipe?.length || 0}</Badge>
                        </Button>
                      </td>
                      <td className="text-muted small">{formatDate(videoDate)}</td>
                      <td className="fw-bold">{videoTitle}</td>
                      <td><Badge bg="light" text="dark" className="border text-uppercase">{(video.tipo || DICT.CONTENT_SING.toLowerCase())}</Badge></td>
                      <td>
                        <div className="d-flex align-items-center">
                          <i className="bi bi-eye-fill me-2 text-primary"></i>
                          {Number(videoViews).toLocaleString('pt-BR')}
                        </div>
                      </td>
                      <td className="fw-bold text-success">
                        R$ {totalBonus.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </td>
                      <td><Badge className={`${badgeColor} text-uppercase`} style={{ fontSize: 'var(--font-size-xs)' }}>{video.status}</Badge></td>
                      {isAdmin && (
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            title="Recalcular pagamentos da equipe para este vídeo"
                            onClick={async () => {
                              if (isDemo) return toast.info("Simulação: recálculo não afeta dados reais.");
                              try {
                                await recalcularBonus(video.id);
                                toast.success(`${DICT.REWARD} recalculado!`);
                              } catch { toast.error('Falha no recálculo.'); }
                            }}
                          >
                            <i className="bi bi-arrow-clockwise me-1"></i>
                            Recalcular
                          </Button>
                        </td>
                      )}
                    </tr>
                    <tr>
                      <td colSpan={isAdmin ? 8 : 7} className="p-0 border-0">
                        <Collapse in={expanded[video.id]}>
                          <div className="p-3" style={{ backgroundColor: '#fcfcfc', borderLeft: '4px solid var(--brand-primary)' }}>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <span className="small fw-bold text-secondary">COLABORADORES ATRIBUÍDOS:</span>
                              {isAdmin && (
                                <Button size="sm" variant="primary" className="py-0 px-2" onClick={() => abrirModal(video.id)}>
                                  <i className="bi bi-plus"></i> Atribuir
                                </Button>
                              )}
                            </div>
                            <div className="row g-2">
                              {(video.equipe || []).map(colab => (
                                <div key={colab.id} className="col-md-4">
                                  <div className="p-2 border rounded bg-white d-flex justify-content-between align-items-center">
                                    <div className="small">
                                      <div className="fw-bold text-uppercase">{colab.nome}</div>
                                      <div className="text-muted">{colab.cargo} | R$ {Number(colab.bonus || 0).toFixed(2)}</div>
                                    </div>
                                    {isAdmin && (
                                      <Button variant="link" className="text-danger p-0" onClick={() => removerMembro(video.id, colab.id)}>
                                        <i className="bi bi-x-circle"></i>
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              ))}
                              {(!video.equipe || video.equipe.length === 0) && (
                                <div className="col-12 text-muted small fst-italic">Nenhum colaborador atribuído a este {DICT.CONTENT_SING.toLowerCase()}.</div>
                              )}
                            </div>
                          </div>
                        </Collapse>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Paginação Estilizada */}
        <div className="d-flex justify-content-between align-items-center mt-3">
          <Form.Select
            size="sm"
            style={{ width: 'auto' }}
            value={limite}
            onChange={e => {
              setLimite(validatePageSize(Number(e.target.value)));
              setPagina(1);
            }}
          >
            {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n} por página</option>)}
          </Form.Select>
          <div className="small text-muted">
            <Button variant="outline-primary" size="sm" disabled={pagina <= 1} onClick={() => setPagina(p => p - 1)} className="border-0">
              <i className="bi bi-chevron-left"></i>
            </Button>
            <span className="mx-2 fw-bold">Página {pagina} de {totalPaginas}</span>
            <Button variant="outline-primary" size="sm" disabled={pagina >= totalPaginas} onClick={() => setPagina(p => p + 1)} className="border-0">
              <i className="bi bi-chevron-right"></i>
            </Button>
          </div>
        </div>
      </div>

      <AddMemberModal
        show={modalAberto}
        onHide={() => setModalAberto(false)}
        onAdd={adicionarMembro}
        videoId={videoSelecionado}
      />
      <ToastContainer position="bottom-right" theme="colored" autoClose={2000} />
    </div>
  );
};

export default VideoTable;